import { useMemo, useState } from 'preact/hooks';
import $ from 'jquery';
import facetValueIdGet from './facetValueId';

const useApi = (apiFactory, initialState) => {
  const [state, setState] = useState(initialState);
  return useMemo(() => apiFactory({ state, setState }), [
    state,
    setState,
    apiFactory,
  ]);
};

const facetApiFactory = ({ state, setState }) => {
  const addFacet = (facets) => {
    setState((prevState) => {
      if (typeof facets === 'object') {
        // eslint-disable-next-line no-param-reassign
        facets = new Array(facets);
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const facet of facets) {
        if (!Object.keys(prevState.facets).includes(facet.id)) {
          if (!facet.values) {
            facet.values = {};
          }
          // eslint-disable-next-line no-param-reassign
          prevState.facets[facet.id] = facet;
        }
      }
      return { ...prevState };
    });
  };

  const addFacetValue = (facetValues) => {
    setState((prevState) => {
      if (typeof facetValues === 'object') {
        // eslint-disable-next-line no-param-reassign
        facetValues = new Array(facetValues);
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const facetValue of facetValues) {
        if (prevState.facets[facetValue.facetId]) {
          if (
            !Object.keys(prevState.facets[facetValue.facetId].values).includes(
              facetValue.id
            )
          ) {
            // eslint-disable-next-line no-param-reassign
            prevState.facets[facetValue.facetId].values[facetValue.id] = {
              ...facetValue,
              count: 0,
            };
          }
          // eslint-disable-next-line no-param-reassign
          prevState.facets[facetValue.facetId].values[facetValue.id].count += 1;
        }
      }

      return { ...prevState };
    });
  };

  const facetValueToggleStatus = (facetValue) => {
    setState((prevState) => {
      if (
        prevState.facets[facetValue.facetId] &&
        prevState.facets[facetValue.facetId].values[facetValue.id]
      ) {
        // eslint-disable-next-line no-param-reassign
        prevState.facets[facetValue.facetId].values[
          facetValue.id
        ].active = !prevState.facets[facetValue.facetId].values[facetValue.id]
          .active;
        // eslint-disable-next-line no-param-reassign
        prevState.facets[facetValue.facetId].active =
          prevState.facets[facetValue.facetId].values[facetValue.id].active;

        // Set equivalent facet values.
        if (
          prevState.facets[facetValue.facetId].values[facetValue.id].equivalency
        ) {
          const { facetId, id } = prevState.facets[facetValue.facetId].values[
            facetValue.id
          ].equivalency;
          // eslint-disable-next-line no-param-reassign
          prevState.facets[facetId].values[id].active = !prevState.facets[
            facetId
          ].values[id].active;

          // eslint-disable-next-line no-param-reassign
          prevState.facets[facetId].active =
            prevState.facets[facetId].values[id].active;

          if (prevState.facets[facetId] && !prevState.facets[facetId].active) {
            // eslint-disable-next-line no-param-reassign
            prevState.facets[facetId].active = Object.values(
              prevState.facets[facetId].values
            ).some((value) => value.active === true);
          }
        }
      }

      if (
        prevState.facets[facetValue.facetId] &&
        !prevState.facets[facetValue.facetId].active
      ) {
        // eslint-disable-next-line no-param-reassign
        prevState.facets[facetValue.facetId].active = Object.values(
          prevState.facets[facetValue.facetId].values
        ).some((value) => value.active === true);
      }

      // eslint-disable-next-line no-use-before-define
      return { ...prevState, ...apply(true) };
    });
  };

  const setFacetValueCount = (facetValue) => {
    setState((prevState) => {
      if (
        prevState.facets[facetValue.facetId] &&
        prevState.facets[facetValue.facetId].values[facetValue.id]
      ) {
        // eslint-disable-next-line no-param-reassign
        prevState.facets[facetValue.facetId].values[facetValue.id].count =
          facetValue.count;
      }
      return { ...prevState };
    });
  };

  const setMeasurementSystem = (system) => {
    setState((prevState) => {
      // eslint-disable-next-line no-param-reassign
      prevState.measurementSystem = system;
      return { ...prevState };
    });
  };

  // eslint-disable-next-line consistent-return
  const setFacets = (returnState, defaultActive) => {
    const agGrid =
      window.Drupal && window.Drupal.behaviors && window.Drupal.behaviors.agGrid
        ? window.Drupal.behaviors.agGrid
        : null;

    let agGridInstance =
      agGrid && agGrid.state && agGrid.state.instances
        ? agGrid.state.instances.filter(
            (instance) => instance.id === state.agGridInstanceId
          )
        : null;
    if (agGridInstance) {
      [agGridInstance] = agGridInstance;

      // eslint-disable-next-line no-param-reassign
      state.totalResults =
        agGridInstance.gridOptions.api.rowModel.rowsToDisplay.length;

      // eslint-disable-next-line no-param-reassign
      state.totalItems = agGridInstance.gridOptions.rowData.length;

      // eslint-disable-next-line no-param-reassign
      state.measurementSystem =
        agGridInstance.gridOptions.metadata.showMeasureSys;

      if (state.totalItems > 1) {
        const { facets } = state;
        const { facetLabels } = state;
        const reInitializedCounter = [];
        const getMeasurementSystem = (facetId) => {
          let measureSys = null;
          const id = facetId.toLowerCase();
          if (id.indexOf('imperial') !== -1) {
            measureSys = 'imperial';
          }
          if (id.indexOf('metric') !== -1) {
            measureSys = 'metric';
          }
          return measureSys;
        };

        // eslint-disable-next-line no-restricted-syntax
        for (const row of agGridInstance.gridOptions.api.rowModel
          .rowsToDisplay) {
          // eslint-disable-next-line no-restricted-syntax
          for (const facetId of Object.keys(row.data)) {
            if (state.exclude.includes(facetId)) {
              // eslint-disable-next-line no-continue
              continue;
            }
            if (!facets[facetId]) {
              if (!facetLabels[facetId]) {
                const facetLabel = agGridInstance.gridOptions.api.columnModel
                  .getColumnDefs()
                  .filter((columnDef) => columnDef.field === facetId);
                facetLabels[facetId] = facetLabel[0].headerName;
              }
              facets[facetId] = {
                id: facetId,
                active: false,
                label: facetLabels[facetId],
                values: {},
                measurementSystem: getMeasurementSystem(facetId),
              };
            }

            const facetValue = row.data[facetId];
            const facetValueId = facetValueIdGet(facetId, facetValue);
            if (!facets[facetId].values[facetValueId] && facetValue !== '') {
              const isActive =
                defaultActive && defaultActive.includes(facetValueId);
              let image = null;
              if (state.images && state.images[facetValueId]) {
                image = state.images[facetValueId];
              }
              let imagedescription = null;
              // eslint-disable-next-line
              if (
                state.imagesdescriptions &&
                state.imagesdescriptions[facetId] &&
                state.imagesdescriptions[facetId][facetValue]
              ) {
                // eslint-disable-next-line
                imagedescription =
                  state.imagesdescriptions[facetId][facetValue];
              }
              let equivalency = null;
              if (
                state.equivalencies &&
                state.equivalencies[facetId] &&
                state.equivalencies[facetId][facetValue]
              ) {
                equivalency = state.equivalencies[facetId][facetValue];
              }

              facets[facetId].values[facetValueId] = {
                facetId,
                id: facetValueId,
                active: isActive,
                label: facetValue,
                value: facetValue,
                image,
                description: imagedescription,
                equivalency,
              };

              if (isActive) {
                facets[facetId].active = isActive;
              }
            }

            if (facetValue !== '') {
              if (!reInitializedCounter.includes(facetValueId)) {
                facets[facetId].values[facetValueId].count = 0;
                reInitializedCounter.push(facetValueId);
              }
              facets[facetId].values[facetValueId].count += 1;
            }
          }
        }

        // If facet value is not present set count to zero.
        // eslint-disable-next-line no-restricted-syntax
        for (const facetId of Object.keys(facets)) {
          // eslint-disable-next-line no-restricted-syntax
          for (const facetValueId of Object.keys(facets[facetId].values)) {
            if (!reInitializedCounter.includes(facetValueId)) {
              facets[facetId].values[facetValueId].count = 0;
            }
          }
        }

        if (state.sorting) {
          // Use category attribute sort data to sort facets.
          const orderedFacets = {};

          /**
           * Set the numeric logic data sorting to use with sort().
           */
          const numeric = (a, b) => {
            const value1 = parseFloat(
              a.value.replace(',', '').replace(/[A-Za-z]/g, '')
            );
            const value2 = parseFloat(
              b.value.replace(',', '').replace(/[A-Za-z]/g, '')
            );
            return value1 - value2;
          };

          /**
           * Set the alphabetic logic data sorting to use with sort().
           */
          const alphabetic = (a, b) => {
            const value1 = a.value.replace(/[0-9]/g, '');
            const value2 = b.value.replace(/[0-9]/g, '');
            return value1.localeCompare(value2);
          };

          Object.keys(state.sorting).forEach((facetId) => {
            if (facets[facetId]) {
              orderedFacets[facetId] = { ...facets[facetId] };
              const dataSortingChildren = Object.values(
                state.sorting[facetId].children
              );

              if (dataSortingChildren.length > 0) {
                orderedFacets[facetId].values = {};
                dataSortingChildren.forEach((facetValue) => {
                  const facetValueId = facetValueIdGet(facetId, facetValue);
                  if (facets[facetId].values[facetValueId]) {
                    orderedFacets[facetId].values[facetValueId] =
                      facets[facetId].values[facetValueId];
                    delete facets[facetId].values[facetValueId];
                  }
                });
              } else {
                const values = Object.values(orderedFacets[facetId].values)
                  .sort(alphabetic)
                  .sort(numeric);
                orderedFacets[facetId].values = {};
                values.forEach((value) => {
                  orderedFacets[facetId].values[value.id] = value;
                });
              }

              if (facets[facetId].values) {
                orderedFacets[facetId].values = {
                  ...orderedFacets[facetId].values,
                  ...facets[facetId].values,
                };
              }
              delete facets[facetId];
            }
          });
          // eslint-disable-next-line no-param-reassign
          state.facets = { ...facets, ...orderedFacets };
        } else {
          // eslint-disable-next-line no-param-reassign
          state.facets = facets;
        }
      }
    } else {
      console.log('AgGrid instance not found');
    }

    if (returnState) {
      return state;
    }

    setState((prevState) => {
      return { ...prevState, ...state };
    });
  };

  // eslint-disable-next-line consistent-return
  const apply = (returnState) => {
    const getActiveFacets = () => {
      const facets = {};
      // eslint-disable-next-line no-restricted-syntax
      for (const facet of Object.values(state.facets).filter(
        // eslint-disable-next-line no-shadow
        (facet) => facet.active === true
      )) {
        const facetValues = Object.values(facet.values).filter(
          (facetValue) => facetValue.active === true
        );
        let equivalentId = null;
        if (facetValues[0] && facetValues[0].equivalency) {
          equivalentId = facetValues[0].equivalency.facetId;
        }

        facets[facet.id] = {
          ...facet,
          facetValues,
          values: facetValues.map((facetValue) => {
            return facetValue.value;
          }),
          equivalentId,
        };
      }
      return facets;
    };

    const activeFacets = getActiveFacets();
    const agGrid =
      window.Drupal && window.Drupal.behaviors && window.Drupal.behaviors.agGrid
        ? window.Drupal.behaviors.agGrid
        : null;
    if (agGrid) {
      agGrid.setFilters(activeFacets);
    } else {
      console.log('AgGrid not found.');
    }

    if (returnState) {
      return setFacets(true);
    }

    setState((prevState) => {
      return { ...prevState, ...setFacets(true) };
    });
  };

  const clearFacets = () => {
    const newFacets = { ...state.facets };
    Object.keys(state.facets).forEach((facetId) => {
      const newFacet = newFacets[facetId];
      Object.keys(newFacet.values).forEach((valueId) => {
        newFacet.values[valueId].active = false;
      });
      newFacet.active = false;
    });
    setState({ ...state, facets: newFacets, ...apply(true) });
  };

  const isAvailableFacet = (facet, facetValue) => {
    const agGrid =
      window.Drupal && window.Drupal.behaviors && window.Drupal.behaviors.agGrid
        ? window.Drupal.behaviors.agGrid
        : null;

    let notAvailable = facetValue.count === 0 && !facet.active;
    if (!notAvailable) {
      notAvailable =
        facetValue.count === 0 && facetValue.active && state.totalResults > 0;
    }

    if (!notAvailable) {
      notAvailable =
        facetValue.count === 0 &&
        !facetValue.active &&
        agGrid &&
        !agGrid.isAvailableFilterValue({
          id: facetValue.facetId,
          value: facetValue.value,
        });
    }

    if (!notAvailable) {
      notAvailable =
        facetValue.count === state.totalResults && !facetValue.active;
    }

    return !notAvailable;
  };

  const getFacets = () => {
    return Object.values(state.facets);
  };

  const getFacetsImages = () => {
    const facetsImages = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const facet of Object.values(state.facets)) {
      if (!facetsImages[facet.id]) {
        facetsImages[facet.id] = { ...facet };
        facetsImages[facet.id].values = [];
      }
      if (facet.active) {
        facetsImages[facet.id].values = Object.values(facet.values).filter(
          (facetValue) => facetValue.active && facetValue.image !== null
        );
      } else {
        facetsImages[facet.id].values = Object.values(facet.values).filter(
          (facetValue) => facetValue.count > 0 && facetValue.image !== null
        );
      }
    }
    return Object.values(facetsImages);
  };

  const getTotalResults = () => {
    return state.totalResults;
  };

  const getFiltersCount = () => {
    let count = 0;
    const filterProcessed = [];
    const facets = Object.values(state.facets).filter((facet) => facet.active);
    facets.forEach((facet) => {
      if (facet.values) {
        Object.keys(facet.values).forEach((id) => {
          if (
            !(
              facet.values[id].equivalency &&
              filterProcessed.includes(facet.values[id].equivalency.id)
            )
          ) {
            filterProcessed.push(id);
            if (facet.values[id].active) {
              count += 1;
            }
          }
        });
      }
    });
    return count;
  };

  const getTotalItems = () => {
    return state.totalItems;
  };

  const isVisibleInMeasurementSystem = (facet) => {
    return (
      facet.measurementSystem === null ||
      facet.measurementSystem === state.measurementSystem
    );
  };

  /**
   * Set a height to the facets filters.
   * @param {Number} resultsHeight
   */
  const setHeight = (resultsHeight = 0) => {
    const agGridFacetsFilter = $('.ag-grid-facet-filters');
    const agGridFacetsTop = $('.ag-grid-facets-top');
    if (window.innerWidth >= 720) {
      if (agGridFacetsFilter && agGridFacetsTop) {
        const topHeight = agGridFacetsTop.height();
        const filtersHeight = resultsHeight + topHeight;
        agGridFacetsFilter.css('max-height', filtersHeight);
      }
    } else {
      agGridFacetsFilter.css('max-height', 'none');
    }
  };

  return {
    setFacets,
    addFacet,
    addFacetValue,
    setFacetValueCount,
    facetValueToggleStatus,
    apply,
    isAvailableFacet,
    setMeasurementSystem,
    isVisibleInMeasurementSystem,
    setHeight,
    getFacets,
    clearFacets,
    getTotalResults,
    getFiltersCount,
    getFacetsImages,
    getTotalItems,
  };
};

const facetApi = (initialState) => {
  const images = {};
  if (initialState.images) {
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const facetId of Object.keys(initialState.images)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const facetValue of Object.keys(initialState.images[facetId])) {
        if (initialState.images[facetId][facetValue] !== '') {
          const facetValueId = facetValueIdGet(facetId, facetValue);
          images[facetValueId] = initialState.images[facetId][facetValue];
        }
      }
    }
  }
  const equivalencies = {};
  if (initialState.equivalencies) {
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const facetId of Object.keys(initialState.equivalencies)) {
      // eslint-disable-next-line guard-for-in,no-restricted-syntax
      for (const facetValue of Object.keys(
        initialState.equivalencies[facetId]
      )) {
        const facetIdEquivalency =
          initialState.equivalencies[facetId][facetValue].attribute_id;
        const facetValueEquivalency = initialState.equivalencies[facetId][
          facetValue
        ].display_value.replace(',', '.');
        const id = facetValueIdGet(facetIdEquivalency, facetValueEquivalency);
        if (!equivalencies[facetId]) {
          equivalencies[facetId] = {};
        }
        equivalencies[facetId][facetValue] = {
          facetId: facetIdEquivalency,
          value: facetValueEquivalency,
          id,
        };
      }
    }
  }
  return useApi(facetApiFactory, { ...initialState, images, equivalencies });
};

export default facetApi;
